import cx from 'classnames';
import { useSelector } from 'react-redux';
import Avatar from '../Avatar/Avatar';
import MetaRow from '../MetaRow/MetaRow';
import AllergyChip from '../AllergyChip/AllergyChip';
import { InfoIcon, MailIcon, PhoneIcon } from '../icons';
import { getAgeGenderLine, getPatientLanguageLabel } from '@shared/lib/patientDisplay';
import './PatientHero.css';

// Local helpers — `@shared/ui` is not allowed to import from
// `@entities/*` (FSD layer boundary). The same logic lives in
// `entities/patient/lib` for callers that already have a patient
// payload and don't want to duplicate; here we keep a tiny copy
// so PatientHero stays self-contained.
const getInitials = (patient) => {
  if (!patient) return '??';
  const { firstName = '', lastName = '' } = patient;
  const first = (firstName || '').trim().charAt(0);
  const last = (lastName || '').trim().charAt(0);
  return `${last}${first}`.toUpperCase() || '??';
};

const formatFullName = (patient) => {
  if (!patient) return '';
  const { firstName = '', lastName = '' } = patient;
  return [lastName, firstName].map((part) => part?.trim() || '').filter(Boolean).join(', ');
};

const EditIcon = (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
    <path d='M12 20h9' />
    <path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z' />
  </svg>
);

const DEFAULT_LABELS = {
  dob: 'DOB',
  mrn: 'MRN',
  phone: 'Phone',
  language: 'Language',
  insurance: 'Insurance',
  editPatient: 'Edit patient',
  callPatient: 'Call patient',
  emailPatient: 'Email patient',
  noPhoneOnFile: 'No phone on file',
  noEmailOnFile: 'No email on file',
  openInfoPane: 'Patient info pane',
  drugAllergies: 'Drug Allergies',
  anonymousPatient: 'Anonymous patient',
};

/**
 * QHS PatientHero
 * ---------------
 * Page-level patient identity strip used as the header of
 * `pages/patient-dashboard` and `pages/patient-edit`. Richer than
 * the compact `PatientBanner` used on sub-pages: 48px avatar,
 * 22px Lato name, MetaRow with up to five identity fields, drug
 * allergy chip + last-seen line on the right, mailto/tel quick
 * actions, optional edit-mini affordance.
 *
 * Three render branches mirror `PatientBanner`:
 *   - loading without payload — skeleton row
 *   - anonymous patient — minimal strip with business name (DS only)
 *   - regular patient — full strip
 *
 * Presentational. Caller fetches the patient (`usePatient`) and
 * passes payload + loading flag.
 *
 *   <PatientHero
 *     patient={patient}
 *     loading={loading}
 *     labels={{ editPatient: t.editPatient, ... }}
 *     onEdit={() => navigate('/.../edit')}
 *     editDisabled={!canEdit}
 *     // nameAdornment={<EngagementStatusDot ... />} — right of the name
 *     // statusSlot={<EngagementChangeButton ... />} — far-right
 *     //   alerts column, after the quick-actions divider
 *     // bottomSlot={<TabsRow>{tabsNode}{actionNode}</TabsRow>}
 *   />
 */
const PatientHero = ({
  patient,
  loading,
  labels,
  onEdit,
  editDisabled = false,
  onOpenInfoPane,
  className,
  bottomSlot,
  statusSlot,
  nameAdornment,
  nameWrapper,
  dimmed = false,
  hideQuickActions = false,
  ...rest
}) => {
  // Catalog labels sit between the English defaults and any explicit
  // `labels` prop overrides from the caller.
  const i18nCatalog = useSelector(state => state.i18n) || {};
  const heroT = i18nCatalog.patientHero || {};
  const t = { ...DEFAULT_LABELS, ...heroT, ...(labels || {}) };

  if (!patient && loading) {
    return (
      <div className={cx('qhs-hero', className)} {...rest}>
        <div className='qhs-hero__top'>
          <span className='qhs-hero__skeleton-avatar qhs-shimmer' />
          <div>
            <span className='qhs-hero__skeleton-row qhs-shimmer' />
            <span className='qhs-hero__skeleton-row qhs-hero__skeleton-row--sm qhs-shimmer' />
          </div>
          <span />
          <span />
        </div>
        {bottomSlot && <div className='qhs-hero__bottom'>{bottomSlot}</div>}
      </div>
    );
  }

  if (!patient) return null;

  const {
    age,
    gender,
    mrn,
    mrnNumber,
    dateOfBirth,
    phone,
    patientLanguage,
    insurance,
    hasDrugAllergies,
    isAnonymousPatient,
    accountBusinessName,
    email,
  } = patient;

  if (isAnonymousPatient) {
    return (
      <div className={cx('qhs-hero', className)} {...rest}>
        <div className='qhs-hero__top'>
          <Avatar size='lg' initials='--' />
          <div className='qhs-hero__identity'>
            <div className='qhs-hero__name'>{t.anonymousPatient}</div>
            {accountBusinessName && (
              <MetaRow items={[{ value: accountBusinessName }]} />
            )}
          </div>
          <span />
          <span />
        </div>
        {bottomSlot && <div className='qhs-hero__bottom'>{bottomSlot}</div>}
      </div>
    );
  }

  const ageGender = getAgeGenderLine({ age, gender }, i18nCatalog);

  const phoneValue = phone?.phone || '';
  // The single-patient payload names this field `mrnNumber`; older callers
  // may still pass `mrn`. Accept either so the MRN chip renders.
  const mrnValue = mrnNumber || mrn;
  const metaItems = [
    dateOfBirth && { label: t.dob, value: dateOfBirth, mono: true },
    mrnValue && { label: t.mrn, value: mrnValue, mono: true },
    phoneValue && { label: t.phone, value: phoneValue },
    patientLanguage && {
      label: t.language,
      value: getPatientLanguageLabel(patientLanguage, i18nCatalog.patientOptions),
    },
    insurance && { label: t.insurance, value: insurance },
  ].filter(Boolean);

  return (
    <div className={cx('qhs-hero', dimmed && 'qhs-hero--dimmed', className)} {...rest}>
      <div className='qhs-hero__top'>
        <Avatar size='lg' initials={getInitials(patient)} />
        <div className='qhs-hero__identity'>
          <div className='qhs-hero__name'>
            {/* Caller-provided adornment right of the name (e.g. the
                engagement presence dot) and optional wrapper around
                name + adornment (e.g. a hover menu) — generic nodes,
                @shared/ui stays unaware of what they mean. */}
            {(() => {
              const nameCore = (
                <>
                  <span>{formatFullName(patient)}</span>
                  {nameAdornment}
                </>
              );
              return nameWrapper ? nameWrapper(nameCore) : nameCore;
            })()}
            {ageGender && <span className='qhs-hero__age'>{ageGender}</span>}
            {onEdit && (
              <button
                type='button'
                className='qhs-hero__edit-mini'
                aria-label={t.editPatient}
                onClick={onEdit}
                disabled={editDisabled}
                title={t.editPatient}
              >
                {EditIcon}
              </button>
            )}
          </div>
          <MetaRow items={metaItems} />
        </div>
        {hideQuickActions ? (
          <span />
        ) : (
        <div className='qhs-hero__quick-actions'>
          {/* href=undefined keeps the link out of the tab order when disabled.
              aria-disabled is announced by AT. CSS pointer-events:none (in
              PatientHero.css) covers the mouse path. The onClick guard is the
              defence-in-depth layer for any programmatic-focus + Enter path. */}
          <a
            className='qhs-hero__qa'
            href={email ? `mailto:${encodeURIComponent(email)}` : undefined}
            aria-disabled={!email}
            aria-label={email ? t.emailPatient : t.noEmailOnFile}
            title={email ? t.emailPatient : t.noEmailOnFile}
            onClick={email ? undefined : (e) => e.preventDefault()}
          >
            <MailIcon />
          </a>
          <a
            className='qhs-hero__qa'
            href={phoneValue ? `tel:${phoneValue.replace(/[^\d+]/g, '')}` : undefined}
            aria-disabled={!phoneValue}
            aria-label={phoneValue ? t.callPatient : t.noPhoneOnFile}
            title={phoneValue ? t.callPatient : t.noPhoneOnFile}
            onClick={phoneValue ? undefined : (e) => e.preventDefault()}
          >
            <PhoneIcon />
          </a>
          {onOpenInfoPane && (
            <button
              type='button'
              className='qhs-hero__qa'
              aria-label={t.openInfoPane}
              title={t.openInfoPane}
              onClick={onOpenInfoPane}
            >
              <InfoIcon />
            </button>
          )}
        </div>
        )}
        <div className='qhs-hero__alerts'>
          {/* Caller-provided status content (e.g. the engagement strip)
              anchors the far-right column, after the quick-actions
              divider: the fixed-width icon group keeps a stable
              position while this variable-width column grows leftward.
              Generic node — @shared/ui stays unaware of what the
              caller renders here. */}
          {statusSlot}
          {hasDrugAllergies && <AllergyChip genericLabel={t.drugAllergies} />}
        </div>
      </div>
      {bottomSlot && <div className='qhs-hero__bottom'>{bottomSlot}</div>}
    </div>
  );
};

export default PatientHero;
